import { Button, Flex, TextAreaField, Typography } from '@iheartjane/reefer';

import { ActivitiesField } from '../activitiesField/activitiesField';
import { FeelingsField } from '../feelingsField/feelingsField';
import { RateAndReviewModal } from '../rateAndReviewModal/rateAndReviewModal';
import { isReviewSubmittable } from '../reviewFormData';
import type { ReviewFormData } from '../reviewFormData';
import type { ReviewOption } from '../reviewOptionsField/reviewOptionsField';
import { StarRatingField } from '../starRatingField/starRatingField';

import sharedStyles from '../rateAndReview.module.css';
import styles from './rateAndReviewWizard.module.css';

export const RATE_AND_REVIEW_WIZARD_STEP_COUNT = 4;

export interface RateAndReviewWizardProps {
  activityOptions: ReviewOption[];
  feelingOptions: ReviewOption[];
  isSubmitting?: boolean;
  onChange: (data: ReviewFormData) => void;
  /** Fired with the step to move to. */
  onStepChange: (step: number) => void;
  onSubmit: () => void;
  /** Zero-based index of the current step. */
  step: number;
  testId?: string;
  value: ReviewFormData;
}

/**
 * Rate & Review body that asks one question per step (A/B test variant B).
 * Renders inside `RateAndReviewModal`, the caller owns the form state, the current step and the submit request.
 */
export function RateAndReviewWizard({
  activityOptions,
  feelingOptions,
  isSubmitting = false,
  onChange,
  onStepChange,
  onSubmit,
  step,
  testId = 'rate-and-review-wizard',
  value,
}: RateAndReviewWizardProps) {
  const update = (patch: Partial<ReviewFormData>) =>
    onChange({ ...value, ...patch });

  const isLastStep = step === RATE_AND_REVIEW_WIZARD_STEP_COUNT - 1;
  const canSubmit = isReviewSubmittable(value);

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <Flex
            className={sharedStyles['primaryStars']}
            justifyContent="center"
          >
            <StarRatingField
              onChange={(rating) => update({ rating })}
              value={value.rating}
            />
          </Flex>
        );
      case 1:
        return (
          <ActivitiesField
            onChange={(activities) => update({ activities })}
            options={activityOptions}
            selectedIds={value.activities}
          />
        );
      case 2:
        return (
          <FeelingsField
            onChange={(feelings) => update({ feelings })}
            options={feelingOptions}
            selectedIds={value.feelings}
          />
        );
      default:
        return (
          // Mirrors RateAndReviewForm's comment field; fine to diverge while the A/B test runs.
          <TextAreaField
            disableMobileInputStyling
            label={
              <>
                Tell us about your experience.{' '}
                <Typography as="span" color="grays-mid">
                  (Optional)
                </Typography>
              </>
            }
            name="comment"
            onChange={(comment) => update({ comment })}
            resize={false}
            rows={5}
            typography="body-semibold"
            value={value.comment}
            width="100%"
          />
        );
    }
  };

  return (
    <>
      <RateAndReviewModal.Body>
        <Flex data-testid={testId} flexDirection="column" gap={24} width="100%">
          <div
            aria-label={`Step ${step + 1} of ${RATE_AND_REVIEW_WIZARD_STEP_COUNT}`}
            aria-valuemax={RATE_AND_REVIEW_WIZARD_STEP_COUNT}
            aria-valuemin={1}
            aria-valuenow={step + 1}
            className={styles['progress']}
            role="progressbar"
          >
            {Array.from(
              { length: RATE_AND_REVIEW_WIZARD_STEP_COUNT },
              (_, index) => (
                <span
                  key={index}
                  className={
                    index <= step
                      ? styles['segmentComplete']
                      : styles['segment']
                  }
                />
              )
            )}
          </div>
          {renderStep()}
        </Flex>
      </RateAndReviewModal.Body>
      <RateAndReviewModal.Footer>
        {isLastStep ? (
          <Button
            disabled={!canSubmit}
            label="Submit review"
            loading={isSubmitting}
            onClick={onSubmit}
          />
        ) : (
          <>
            <Button
              disabled={!canSubmit}
              label="Submit review"
              loading={isSubmitting}
              onClick={onSubmit}
              variant="tertiary"
            />
            <Button
              className={styles['next']}
              disabled={!canSubmit || isSubmitting}
              label="Next"
              onClick={() => onStepChange(step + 1)}
            />
          </>
        )}
      </RateAndReviewModal.Footer>
    </>
  );
}
